<template>
  <div>
    <v-data-table
      ref="maintenanceTableAssembly"
      v-model="maintenanceState.selected"
      :headers="headers"
      :items="maintenanceState.data"
      sort-by="id"
      class="elevation-2"
      show-select
      show-expand
      single-expand
      :loading="maintenanceState.isBusy"
      :search="search"
      item-key="id"
      @contextmenu:row="openContextMenu"
      @item-expanded="getPromptData"
    >
      <template v-slot:no-data>
        <h2
          class="primary--text"
        >
          No {{ displayAsPlural }} Available
        </h2>
      </template>

      <template v-slot:no-results>
        <h3
          class="primary--text"
        >
          No matching {{ displayAsSingular }} data found containing '{{ search }}'
        </h3>
      </template>

      <template v-slot:loading>
        <h2
          class="primary--text"
        >
          Loading {{ displayAsPlural }}... Please wait
        </h2>
      </template>

      <!-- Search -->
      <template v-slot:top>
        <v-container
          class="pa-0 ma-0"
        >
          <v-row
            no-gutters
            class="pa-0 ma-0"
          >
            <v-col
              cols="12"
              sm="10"
              class="pa-0 ma-0 text-center"
            >
              <v-text-field
                v-if="maintenanceState.data.length > 0"
                v-model="search"
                prepend-icon="mdi-magnify"
                label="Search"
                single-line
                hide-details
                clearable
                class="pl-10 pr-10"
                @click="input_onClick"
              />
            </v-col>

            <v-col
              cols="12"
              sm="12"
              md="2"
              class="pa-0 ma-0 text-right"
            >
              <v-btn
                small
                color="primary"
                class="ml-0 mt-1 mr-1 mb-0 pt-4 pb-4"
                :disabled="disableNew"
                @click="menuNew_onClick"
              >
                New {{ displayAsSingular }}
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </template>

      <template v-slot:item.data-table-select="{ isSelected, select }">
        <v-simple-checkbox
          color="primary"
          :value="isSelected"
          @input="select($event)"
        />
      </template>

      <template v-slot:item.id="{ item }">
        <div
          class="font-weight-medium text-body-1"
          @dblclick="row_onDblClick(item)"
        >
          {{ item.id }}
        </div>
      </template>

      <template v-slot:item.description="{ item }">
        <div
          class="font-weight-medium text-body-1"
          @dblclick="row_onDblClick(item)"
        >
          {{ item.description }}
        </div>
      </template>

      <template v-slot:item.prompt_description="{ item }">
        <div
          class="font-weight-medium text-body-1"
          @dblclick="row_onDblClick(item)"
        >
          {{ item.prompt_description }}
        </div>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-icon
          small
          color="primary"
          class="mr-2"
          @click="openEditDialog(item)"
        >
          mdi-pencil
        </v-icon>

        <v-icon
          small
          color="primary"
          @click="openDeleteDialog(item)"
        >
          mdi-delete
        </v-icon>
      </template>

      <template v-slot:expanded-item="{ item }">
        <td
          :colspan="headers.length+1"
          @contextmenu:row="openContextMenu"
        >
          <v-container
            class="py-0"
          >
            <v-row
              no-gutters
            >
              <v-col
                cols="12"
                sm="10"
                offset-sm="1"
                class="text-right pt-5 pb-5"
              >
                <prompt-table :parent-item="item" />
              </v-col>
            </v-row>
          </v-container>
        </td>
      </template>
    </v-data-table>

    <!-- Dialogs -->
    <v-dialog
      ref="editDialog"
      :key="renderKey"
      v-model="dialogEdit"
      :width="dialogWidth"
      scrollable
      @keydown.esc.prevent="cancelEdit"
      @keydown.enter.prevent="saveEdit"
    >
      <edit-assembly
        ref="formEditAssembly"
        v-model="editedItem"
        :edited-index="editedIndex"
        :showTitle="true"
        :showActions="true"
        @cancel="cancelEdit"
        @close="closeDialog"
      />
    </v-dialog>

    <delete-dialog
      ref="maintenanceDeleteDialog"
      :maintenance-items="maintenanceState"
      :display-as-singular="displayAsSingular"
      :display-as-plural="displayAsPlural"
      @delete="item_onDelete()"
      @cancel="cancelDelete()"
    />

    <!-- Menus -->
    <context-menu
      ref="maintenanceContextMenu"
      :maintenance-items="maintenanceState"
      :display-as-singular="displayAsSingular"
      :display-as-plural="displayAsPlural"
      @newitem="menuNew_onClick"
      @edititem="menuEdit_onClick"
      @deleteitem="menuDelete_onClick"
    />

    <div style="display: none;">
      <v-list-item
        id="navToChildMaintenance"
        to="../takeoff"
      />
    </div>
  </div>
</template>

<script>
  import { get } from 'vuex-pathify'
  import DeleteDialog from '@/components/maintenance/DeleteDialog'
  import ContextMenu from '@/components/maintenance/ContextMenu'
  import PromptTable from './PromptTable'
  import EditAssembly from '@/components/maintenance/forms/EditAssembly'

  export default {
    name: 'AssemblyTable',

    components: {
      DeleteDialog,
      ContextMenu,
      PromptTable,
      EditAssembly,
    },

    data () {
      return {
        dialogEdit: false,
        editedIndex: -1,
        editedItem: {},
        renderKey: 0,
        search: null,
        headers: [],
      }
    },

    computed: {
      appHeaders: get('app/headers'),
      divisions: get('division/divisions'),
      displayAsSingular: get('assembly/displayAsSingular'),
      displayAsPlural: get('assembly/displayAsPlural'),
      maintenanceState: get('assembly/assemblies'),
      dialogWidth () {
        let newWidth = null
        switch (this.$vuetify.breakpoint.name) {
          case 'xs':
            newWidth = null
            break
          case 'sm':
            newWidth = '80%'
            break
          case 'md':
            newWidth = '65%'
            break
          case 'lg':
            newWidth = '55%'
            break
          case 'xl':
            newWidth = '50%'
            break
        }
        return newWidth
      },
      disableNew () {
        let disabled = true

        if (this.divisions.selectedItem) {
          if ({}.hasOwnProperty.call(this.divisions.selectedItem, 'id')) {
            disabled = this.divisions.selectedItem.id.length <= 0
          }
        }

        return disabled
      },
    },

    watch: {
      dialogEdit (val) {
        if (val) {
          this.forceRerender()
        }

        if (this.editedIndex < 0) {
          this.addItem()
        }
        val || this.cancelEdit()
      },
    },

    created () {
      this.initialize()
    },

    methods: {
      setupHeaders () {
        this.maintenanceState.headers.headerId.width = '10%'
        this.maintenanceState.headers.headerDescription.width = '58%'
        this.maintenanceState.headers.headerPromptGroup.width = '30%'

        this.headers = []

        this.headers.push(this.maintenanceState.headers.headerId)
        this.headers.push(this.maintenanceState.headers.headerDescription)
        this.headers.push(this.maintenanceState.headers.headerPromptGroup)
        this.headers.push(this.appHeaders.headerActions)
        this.headers.push(this.appHeaders.headerExpandDetail)
      },

      initialize () {
        this.$store.dispatch('promptgroup/retrieveAll')

        this.setupHeaders()
      },

      getPromptData ({ item, value }) {
        if (value) {
          this.$store.dispatch('prompt/retrieve', item)
        }
      },

      setDialogEdit (val) {
        this.dialogEdit = val
      },

      addItem () {
        this.editedIndex = -1
        this.editedItem = Object.assign({}, this.maintenanceState.empty)

        this.$store.dispatch('user/setStateAsUpdated', this.editedItem)
      },

      forceRerender () {
        this.renderKey += 1
      },

      openEditDialog (dataItem) {
        this.editedIndex = this.maintenanceState.data.indexOf(dataItem)
        this.editedItem = Object.assign({}, dataItem)
        this.dialogEdit = true
      },

      openDeleteDialog (dataItem) {
        this.$refs.maintenanceDeleteDialog.openDeleteDialog(dataItem)
      },

      item_onDelete () {
        this.$store.dispatch('assembly/delete')
      },

      cancelDelete () {
        this.$refs.maintenanceContextMenu.setSelectedMenu(null)
      },

      openContextMenu (evt, selectedRow) {
        this.$refs.maintenanceContextMenu.openContextMenu(evt, selectedRow.item)
      },

      menuNew_onClick () {
        this.addItem()
        this.dialogEdit = true
      },

      menuEdit_onClick (dataItem) {
        this.openEditDialog(dataItem)
      },

      menuDelete_onClick () {
        this.$refs.maintenanceDeleteDialog.setDeleteMultiple(true)
        this.$refs.maintenanceDeleteDialog.setDisplayDialog(true)
      },

      cancelEdit () {
        this.dialogEdit = false
        this.$refs.maintenanceContextMenu.setSelectedMenu(null)
        this.$nextTick(() => {
          this.editedIndex = -1
        })
      },

      saveEdit () {
        this.$refs.formEditAssembly.saveEdit()
      },

      closeDialog () {
        this.dialogEdit = false
      },

      row_onDblClick (selectedItem) {
        this.$store.dispatch('assembly/setSelected', selectedItem)

        this.$nextTick(() => {
          document.getElementById('navToChildMaintenance').click()
        })
      },
    },
  }
</script>

<style scoped>

</style>
